import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import {
  selectEvent,
  selectTimeslot,
  setBreadcrumbs,
  setError,
  setEvents,
  setUser,
  useAppContext,
} from '../../contextProviders/AppContext';
import * as selectors from '../../contextProviders/AppContext/selectors';
import {
  authenticate,
  getEvent,
  getEventHead,
} from '../../services/storeEventApi';
import AvailableTimeSlots from './AvailableTimeSlots';
import Divider from '../../components/Divider';
import ConditionalRender from '../../components/ConditionalRender';
import {
  baseUrl,
  getEventIdFromSlug,
  isObjectEmpty,
  isTimeSlotFullyBooked,
  toTimestamp,
} from '../../utils';
import LoginAlert from '../../components/LoginAlert';
import { BOOK_EVENT, CANCELLED } from '../../enums';
import {
  REGISTERED,
  WAITING_LIST,
  CHECKIN,
  SELF_CHECKIN,
} from '../Register/enums';
import Message from '../../components/Message';
import CancelRegistration from './CancelRegistration';
import getFirstAvailableTimeSlot from './getFirstAvailableTimeSlot';
import CancelledEventMessage from './CancelledEventMessage';
import LastRegistrationDate from './LastRegistrationDate';
import Seo from '../../components/Seo/Seo';
import { EVENT_DETAILS } from '../../components/Seo/enums';
import {
  Wrapper,
  Mask,
  EventLocation,
  EventName,
  RegisterButton,
  NoRegistrationRequired,
  EventDescription,
  DigitalEventWrapper,
  GotoDigitalEventWrapper,
} from './style';
import { sendPageView, sendSignUpEvent } from './analytics';
import { isRegistrationDisabled } from './isRegistrationDisabled';
import useScrollToTop from '../../hooks/ScrollToTop';
import EventPrice from './EventPrice';
import RegistrationStartDate from './RegistrationStartDate';
import Focus from '../../components/Focus';
import AspectRatioImage from '../../components/AspectRatioImage';
import Button from '@ingka/button';
import {
  getAuthToken,
  isUserEligibleToRegisterToEvent,
  setAuthToken,
} from '../../services/storeEventApi/utils';

const ACTIVE_REG_STATES = [REGISTERED, WAITING_LIST, CHECKIN, SELF_CHECKIN];

const BookEvent = () => {
  const { useSelector, dispatch } = useAppContext();
  const { eventid } = useParams<{ eventid: string }>();
  const { storeName } = useParams<{ storeName: string }>();
  const eventList = useSelector(selectors.getEvents);
  const selectedStore = useSelector(selectors.getSelectedStore);
  const selectedEvent = useSelector(selectors.getSelectedEvent);
  const selectedTimeslot = useSelector(selectors.getSelectedTimeslot);
  const user = useSelector(selectors.getUser);
  const [registeredToTimeSlot, registrationState] = useSelector(
    selectors.getRegisteredTimeSlotAndState
  );
  const allRegisteredTimeslots = useSelector(
    selectors.getAllRegisteredTimeSlots
  );
  const eventIsDigital = useSelector(selectors.isDigitalEvent);
  const isSignedIn = useSelector(selectors.isSignedIn);
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const eventId = getEventIdFromSlug(eventid);
  const [sessionToken, setSessionToken] = useState<any>('');
  const [userSelectedTimeslot, setUserSelectedTimeslot] = useState(false);

  useScrollToTop();

  useEffect(() => {
    if (eventId) {
      dispatch(selectEvent(eventId));
      setUserSelectedTimeslot(false);
    }
  }, [eventId, dispatch]);

  useEffect(() => {
    let mounted = true;
    const fetchEvent = async () => {
      try {
        // first check if event exist, otherwise redirect to parent store
        await getEventHead(eventId);
      } catch (e) {
        const pathname = history.location.pathname;
        const temp2 = baseUrl + pathname;
        const hrefBase = window.location.href.slice(0, -temp2.length);

        const redirectUrl = hrefBase + baseUrl + '/' + storeName + '/';
        // Navigate to the redirect url
        window.location.href = redirectUrl;
      }

      try {
        setAuthToken(setSessionToken);
        if (getAuthToken()) {
          const userInfo = await authenticate();
          dispatch(setUser(userInfo.format()));
        }
        if (sessionToken) {
          const evnt = await getEvent(eventId);
          if (evnt && mounted) {
            dispatch(setEvents([evnt], false));
            dispatch(selectEvent(evnt.id));
          }
        }
      } catch (e: any) {
        dispatch(setError(e.code));
        Sentry.captureException(e);
      }
    };

    if (eventList.length === 0) {
      fetchEvent();
    }

    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId, eventList.length, sessionToken]);

  useEffect(() => {
    if (isObjectEmpty(selectedTimeslot)) {
      const isMultiReg = selectedEvent?.allowMultipleRegistrations ?? false;
      if (isMultiReg) {
        // When multi-registration is enabled, prioritize first available unregistered timeslot
        const firstUnregistered = selectedEvent?.timeSlots?.find(
          (ts: any) => !ts.registration || !ACTIVE_REG_STATES.includes(ts.registration.state)
        );
        const timeslotId = firstUnregistered?.timeslotId
          || getFirstAvailableTimeSlot(selectedEvent?.timeSlots);
        if (timeslotId) {
          dispatch(selectTimeslot(timeslotId));
        } else if (selectedEvent?.timeSlots?.length > 0) {
          dispatch(selectTimeslot(selectedEvent.timeSlots[0].timeslotId));
        }
      } else if (registeredToTimeSlot) {
        
        dispatch(selectTimeslot(registeredToTimeSlot));
      } else {
        const timeslotid = getFirstAvailableTimeSlot(selectedEvent?.timeSlots);
        if (timeslotid) {
          dispatch(selectTimeslot(timeslotid));
        } else if (selectedEvent?.timeSlots?.length > 0) {
          dispatch(selectTimeslot(selectedEvent?.timeSlots[0].timeslotId));
        }
      }
    }
  }, [selectedEvent, registeredToTimeSlot, selectedTimeslot, dispatch]);

  useEffect(() => {
    if (selectedEvent && selectedStore) {
      sendPageView(selectedEvent);
      dispatch(
        setBreadcrumbs([
          { text: selectedStore.storeName, url: selectedStore.slug },
          {
            text: selectedEvent.eventDetails?.eventName as string,
            url: `${selectedStore.slug}/${selectedEvent?.urlId}`,
          },
        ])
      );
    }
  }, [selectedEvent, selectedStore, dispatch]);

  if (!selectedEvent) {
    return null;
  }

  const onSelectTimeslot = (timeslotId: string) => {
    setUserSelectedTimeslot(true);
    dispatch(selectTimeslot(timeslotId));
  };

  const hasTimeSlots = selectedEvent.timeSlots.length > 0;
  const isEventCancelled = selectedEvent.visibilityState === CANCELLED;
  const isRegistrationRequired =
    selectedEvent?.registrationSettings?.registrationRequired;

  const allowMultipleRegistrations =
    selectedEvent?.allowMultipleRegistrations ?? false;

  const hasMultipleRegistrations =
    !allowMultipleRegistrations && allRegisteredTimeslots.length > 1;

  const isUserEligibleToRegister = isUserEligibleToRegisterToEvent(
    user,
    selectedEvent?.loyaltyEvent
  );

  const isEventFullyBooked = selectedEvent.timeSlots.every(
    isTimeSlotFullyBooked
  );

  const isRegistrationStarted =
    !selectedEvent?.registrationSettings?.utcRegistrationStarts ||
    toTimestamp(selectedEvent?.registrationSettings?.utcRegistrationStarts) <
      Date.now();
  
  const isSelectedTimeslotRegistered = Boolean(
    selectedTimeslot?.registration &&
    ACTIVE_REG_STATES.includes(selectedTimeslot.registration.state)
  );

  const selectedTimeslotRegState = selectedTimeslot?.registration?.state;

  const showSignInMessage =
    hasTimeSlots &&
    isRegistrationRequired &&
    (!isSignedIn || !isUserEligibleToRegister);

  const isTimeSlotsSelectable =
    selectedEvent.timeSlots.length > 1 &&
    isRegistrationStarted &&
    (!isEventFullyBooked || (allowMultipleRegistrations && registeredToTimeSlot) || hasMultipleRegistrations) &&
    isRegistrationRequired &&
    !isEventCancelled;

  const showRegistrationButton =
    hasTimeSlots &&
    isSignedIn &&
    isUserEligibleToRegister &&
    isRegistrationStarted &&
    !isEventCancelled &&
    isRegistrationRequired &&
    !(allowMultipleRegistrations ? isSelectedTimeslotRegistered : registeredToTimeSlot);

  const showGotoEventButton =
    eventIsDigital &&
    selectedEvent?.location?.url &&
    (!isRegistrationRequired || registeredToTimeSlot);

  const onRegisterForEvent = () => {
    if (selectedTimeslot) {
      sendSignUpEvent(selectedEvent);
      history.push(`/${selectedStore.slug}/${eventId}/register`);
    }
  };

  const gotoDigitalEvent = () => {
    window.open(selectedEvent.location.url, '_blank');
  };

  return (
    <Focus>
      <Seo
        event={selectedEvent}
        page={EVENT_DETAILS}
        canonicalPath={location.pathname}
      />
      <ConditionalRender
        predicate={
          hasTimeSlots &&
          isRegistrationRequired &&
          !isEventCancelled &&
          !isUserEligibleToRegister
        }
      >
        <LoginAlert
          margin="0 0 var(--space-150) 0"
          initiator={BOOK_EVENT}
          loyaltyEventType={selectedEvent?.loyaltyEvent}
        />
      </ConditionalRender>
      <ConditionalRender predicate={isEventCancelled}>
        <CancelledEventMessage />
      </ConditionalRender>
      <Wrapper>
        <ConditionalRender predicate={isEventCancelled}>
          <Mask />
        </ConditionalRender>
        <div>
          <AspectRatioImage
            src={`${selectedEvent.eventImagePath}?f=m`}
            srcSet={`${selectedEvent.eventImagePath}?f=m 500w,${selectedEvent.eventImagePath}?f=xs 400w,${selectedEvent.eventImagePath}?f=xxs 300w`}
            ratio="square"
            alt=""
          />
        </div>
        <div>
          <EventName>{selectedEvent?.eventDetails.eventName}</EventName>
          <p>{selectedEvent?.eventDetails.eventIntroduction}</p>
          <EventLocation>
            {eventIsDigital
              ? t('event.onlineEvent')
              : selectedEvent?.location.store.storeName}
          </EventLocation>

          <EventPrice event={selectedEvent} />

          <ConditionalRender predicate={!isRegistrationRequired}>
            <NoRegistrationRequired>
              {t('event.noRegistrationRequired')}
            </NoRegistrationRequired>
          </ConditionalRender>

          <AvailableTimeSlots
            timeslots={selectedEvent.timeSlots}
            onSelect={onSelectTimeslot}
            value={selectedTimeslot}
            selectable={isTimeSlotsSelectable}
            timeZone={selectedEvent.timeZone}
            registrationRequired={isRegistrationRequired}
          />

          <RegistrationStartDate
            startDate={selectedEvent.registrationSettings?.registrationStarts}
            utcStartDate={toTimestamp(
              selectedEvent.registrationSettings?.utcRegistrationStarts
            )}
            timeZone={selectedEvent.timeZone}
          />

          <LastRegistrationDate
            closeDate={selectedTimeslot?.registrationClosedDate}
            timeZone={selectedEvent.timeZone}
            registrationRequired={
              selectedEvent.registrationSettings?.registrationRequired
            }
          />

          <ConditionalRender predicate={showRegistrationButton}>
            <RegisterButton
              aria-describedby="sign-in-message"
              type="primary"
              text={
                selectedTimeslot?.registerToWaitingList
                  ? t('event.joinWaitingList')
                  : t('event.register')
              }
              onClick={onRegisterForEvent}
              disabled={isRegistrationDisabled(
                selectedTimeslot,
                isEventFullyBooked
              )}
            />
          </ConditionalRender>
          <ConditionalRender predicate={showSignInMessage}>
            <LoginAlert
              margin="var(--space-300) 0 0"
              initiator={BOOK_EVENT}
              loyaltyEventType={selectedEvent?.loyaltyEvent}
            />
          </ConditionalRender>
          <ConditionalRender predicate={(allowMultipleRegistrations || hasMultipleRegistrations) ? isSelectedTimeslotRegistered : registeredToTimeSlot}>
            <CancelRegistration 
              registrationState={((allowMultipleRegistrations || hasMultipleRegistrations) ? selectedTimeslotRegState : registrationState) || ''}
              timeSlot={selectedTimeslot}
              disabled={(allowMultipleRegistrations || hasMultipleRegistrations) && !userSelectedTimeslot}
            />
          </ConditionalRender>
          <ConditionalRender
            predicate={
              !isEventCancelled && eventIsDigital && !isEventFullyBooked
            }
          >
            <DigitalEventWrapper>
              <Message subtle>{t('event.onlineEventMsg')}</Message>
            </DigitalEventWrapper>
          </ConditionalRender>

          <Divider margin="var(--space-300) 0 var(--space-300) 0" />
          <h2>{t('event.description')}</h2>
          <EventDescription>
            {selectedEvent.eventDetails.eventDescription}
          </EventDescription>
          <ConditionalRender predicate={showGotoEventButton}>
            <GotoDigitalEventWrapper>
              <Button
                type="primary"
                text={t('event.gotoDigitalEvent')}
                onClick={gotoDigitalEvent}
              />
            </GotoDigitalEventWrapper>
          </ConditionalRender>
        </div>
      </Wrapper>
    </Focus>
  );
};

export default BookEvent;
